import { useQuery } from "@tanstack/react-query";

import { adminApi } from "apis";
import {
  PatientsPaginationRequestDtoGenderEnum,
  PatientsPaginationRequestDtoSortByEnum,
  PatientsPaginationRequestDtoSortOrderEnum,
  PatientsPaginationRequestDtoStatusEnum,
} from "libs/cellera-sdk";
import type { Patient } from "types";
import { mapPatientDtoToPatient, parseUTCDate } from "utils";

export interface GetPatientsParams {
  page: number;
  pageSize: number;
  search?: string;
  gender?: PatientsPaginationRequestDtoGenderEnum[];
  status?: PatientsPaginationRequestDtoStatusEnum[];
  birthDateFrom?: string;
  birthDateTo?: string;
  sortBy?: PatientsPaginationRequestDtoSortByEnum;
  sortOrder?: PatientsPaginationRequestDtoSortOrderEnum;
}

interface UsePatientsResult {
  patients: Patient[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const usePatients = (params: GetPatientsParams): UsePatientsResult => {
  const queryKey = [
    "admin-patients",
    params.page,
    params.pageSize,
    params.search,
    params.gender?.join(","),
    params.status?.join(","),
    params.birthDateFrom,
    params.birthDateTo,
    params.sortBy,
    params.sortOrder,
  ];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await adminApi.getAllPatients({
        patientsPaginationRequestDto: {
          pageSize: params.pageSize || 10,
          page: (params.page || 0) + 1,
          search: params.search,
          gender:
            params.gender && params.gender.length > 0
              ? params.gender
              : undefined,
          status:
            params.status && params.status.length > 0
              ? params.status
              : undefined,
          birthDateFrom: params.birthDateFrom
            ? parseUTCDate(params.birthDateFrom)
            : undefined,
          birthDateTo: params.birthDateTo
            ? parseUTCDate(params.birthDateTo)
            : undefined,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
        },
      });

      return {
        patients: (response.patients ?? []).map(mapPatientDtoToPatient),
        totalCount:
          response.pagination?.total ?? response.patients?.length ?? 0,
      };
    },
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    patients: data?.patients ?? [],
    totalCount: data?.totalCount ?? 0,
    loading: isLoading,
    error: error ? (error as Error).message : null,
    refetch,
  };
};

export default usePatients;
